<template>
  <BaseModal
    v-model="isOpen"
    class="truck-editor-modal"
    :clickOutsideClose="false"
  >
    <FileSelector
      v-if="fileTarget"
      v-model="showFileSelector"
      type="assets"
      :subcategory="
        fileTarget === 'datasheet' ? 'truck_datasheet' : 'truck_image'
      "
      :uploadOnly="fileTarget === 'datasheet'"
      @fileUploaded="onFile"
      @selectedFile="onFile"
    />

    <template v-slot:header>
      <div class="flex items-center space-x-3">
        <div
          class="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"
        >
          <i class="mdi mdi-truck text-red-600 text-xl"></i>
        </div>
        <div>
          <h3 class="text-lg font-semibold text-gray-900">
            {{ truck && truck.id ? truck.name : "Nuevo camión" }}
          </h3>
          <a
            v-if="truck && truck.id && truck.slug"
            :href="`${siteUrl}/camiones/${truck.slug}`"
            target="_blank"
            rel="noopener"
            class="text-sm text-blue-600 hover:underline"
            >Ver en el sitio</a
          >
        </div>
      </div>
    </template>

    <div v-if="!truck" class="flex justify-center py-12">
      <BaseSmallSpinner />
    </div>

    <div v-else class="px-1">
      <nav class="flex space-x-6 border-b border-gray-200 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="[
            'py-3 border-b-2 text-sm font-medium',
            section === tab.value
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          ]"
          @click="section = tab.value"
        >
          {{ tab.label }}
        </button>
      </nav>

      <!-- General -->
      <div
        v-show="section === 'general'"
        class="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <label class="block">
          <span class="field-label">Nombre</span>
          <input v-model="truck.name" type="text" class="field" />
        </label>
        <label class="block">
          <span class="field-label">Serie</span>
          <select v-model="truck.seriesId" class="field">
            <option :value="null">Sin serie</option>
            <option v-for="serie in series" :key="serie.id" :value="serie.id">
              {{ serie.name }}
            </option>
          </select>
        </label>
        <label class="block md:col-span-2">
          <span class="field-label">
            URL: /camiones/<b>{{ truck.slug || "…" }}</b>
          </span>
          <input
            v-model="truck.slug"
            type="text"
            class="field"
            placeholder="Se genera con el nombre si se deja vacío"
          />
          <span v-if="slugChanged" class="text-xs text-yellow-700">
            Cambiar la URL rompe los enlaces que ya apuntan a este camión.
          </span>
        </label>
        <label class="block md:col-span-2">
          <span class="field-label">Descripción</span>
          <textarea
            v-model="truck.description"
            rows="4"
            class="field"
          ></textarea>
        </label>
        <label class="block">
          <span class="field-label">Orden dentro de la serie</span>
          <input v-model.number="truck.sortOrder" type="number" class="field" />
        </label>
        <div class="flex items-end space-x-6 pb-2">
          <label class="inline-flex items-center space-x-2">
            <input
              type="checkbox"
              :checked="truck.status == '1'"
              @change="truck.status = $event.target.checked ? '1' : '0'"
            />
            <span class="text-sm">Publicado</span>
          </label>
          <label class="inline-flex items-center space-x-2">
            <input
              type="checkbox"
              :checked="truck.featured == '1'"
              @change="truck.featured = $event.target.checked ? '1' : '0'"
            />
            <span class="text-sm">Destacado en la portada</span>
          </label>
        </div>
        <div class="md:col-span-2">
          <span class="field-label">Ficha técnica (PDF)</span>
          <div class="flex items-center space-x-3">
            <input
              v-model="truck.datasheetUrl"
              type="text"
              class="field"
              placeholder="Sin ficha"
            />
            <a
              v-if="truck.datasheetUrl"
              :href="assetUrl(truck.datasheetUrl)"
              target="_blank"
              rel="noopener"
              class="btn-secondary whitespace-nowrap"
              >Abrir</a
            >
            <button
              class="btn-secondary whitespace-nowrap"
              @click="pickFile('datasheet')"
            >
              Subir PDF
            </button>
          </div>
        </div>
      </div>

      <!-- Fotos -->
      <div v-show="section === 'images'">
        <p class="text-sm text-gray-600 mb-4">
          La primera foto abre la galería de la ficha. La marcada como
          <b>portada</b> es la que se ve en los listados.
        </p>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            v-for="(image, i) in truck.images"
            :key="image.url + i"
            class="border rounded-lg overflow-hidden"
            :class="
              image.url === truck.imageUrl
                ? 'border-red-500 ring-2 ring-red-200'
                : 'border-gray-200'
            "
          >
            <img
              :src="assetUrl(image.url)"
              class="w-full h-28 object-cover bg-gray-100"
            />
            <div class="p-2 space-y-2">
              <input
                v-model="image.alt"
                type="text"
                class="field text-xs"
                placeholder="Texto alternativo"
              />
              <div class="flex justify-between text-sm">
                <button
                  :disabled="i === 0"
                  title="Antes"
                  @click="move(truck.images, i, -1)"
                >
                  ◀
                </button>
                <button
                  class="text-xs"
                  :class="
                    image.url === truck.imageUrl
                      ? 'text-red-600 font-semibold'
                      : 'text-gray-600'
                  "
                  @click="truck.imageUrl = image.url"
                >
                  {{
                    image.url === truck.imageUrl ? "Portada" : "Usar de portada"
                  }}
                </button>
                <button
                  :disabled="i === truck.images.length - 1"
                  title="Después"
                  @click="move(truck.images, i, 1)"
                >
                  ▶
                </button>
                <button title="Quitar" @click="removeImage(i)">🗑️</button>
              </div>
            </div>
          </div>
          <button
            class="h-44 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-red-400 hover:text-red-600"
            @click="pickFile('image')"
          >
            <i class="mdi mdi-image-plus text-3xl"></i>
            <span class="text-sm">Agregar foto</span>
          </button>
        </div>
      </div>

      <!-- Ficha -->
      <div v-show="section === 'specs'" class="space-y-6">
        <p class="text-sm text-gray-600">
          <b>Descripción</b> y <b>Resumen</b> son las dos tablas junto a las
          fotos; <b>Destacados</b> es la franja roja de cifras. El resto aparece
          en "Todas las características" sólo si tiene filas.
        </p>
        <div v-for="group in specGroups" :key="group.value">
          <div class="flex items-center justify-between mb-2">
            <h4 class="font-semibold text-gray-800">
              {{ group.label }}
              <span class="text-gray-400 font-normal"
                >({{ group.rows.length }})</span
              >
            </h4>
            <button class="text-sm text-blue-600" @click="addSpec(group.value)">
              + Agregar fila
            </button>
          </div>
          <div
            v-for="row in group.rows"
            :key="row.index"
            class="flex items-start space-x-2 mb-2"
          >
            <input
              v-model="truck.specs[row.index].label"
              type="text"
              class="field w-1/3"
              placeholder="Etiqueta (p. ej. Potencia)"
            />
            <textarea
              v-model="truck.specs[row.index].value"
              rows="1"
              class="field flex-1"
              placeholder="Valor"
            ></textarea>
            <button
              class="px-2 py-2 text-gray-500"
              :disabled="row.first"
              title="Subir"
              @click="moveSpec(row.index, -1)"
            >
              ▲
            </button>
            <button
              class="px-2 py-2 text-gray-500"
              :disabled="row.last"
              title="Bajar"
              @click="moveSpec(row.index, 1)"
            >
              ▼
            </button>
            <button
              class="px-2 py-2"
              title="Quitar"
              @click="truck.specs.splice(row.index, 1)"
            >
              🗑️
            </button>
          </div>
        </div>
      </div>

      <!-- SEO -->
      <div v-show="section === 'seo'" class="space-y-4">
        <label class="block">
          <span class="field-label">Título para Google</span>
          <input
            v-model="truck.metaTitle"
            type="text"
            class="field"
            :placeholder="
              `Isuzu ${truck.name || ''} | NKR Motors Group Costa Rica`
            "
          />
        </label>
        <label class="block">
          <span class="field-label">
            Descripción para Google ({{
              (truck.metaDescription || "").length
            }}/160)
          </span>
          <textarea
            v-model="truck.metaDescription"
            rows="3"
            class="field"
            placeholder="Si se deja vacía se usa la descripción"
          ></textarea>
        </label>
      </div>

      <div
        class="flex items-center justify-between pt-6 mt-6 border-t border-gray-200"
      >
        <button
          v-if="truck.id"
          class="text-sm text-red-600 hover:underline"
          :disabled="saving"
          @click="remove()"
        >
          Eliminar camión
        </button>
        <span v-else></span>
        <div class="flex space-x-3">
          <button
            class="btn-secondary"
            :disabled="saving"
            @click="isOpen = false"
          >
            Cerrar
          </button>
          <button
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
            :disabled="saving || !pending"
            @click="save()"
          >
            {{
              saving
                ? "Guardando…"
                : truck.id
                ? "Guardar cambios"
                : "Crear camión"
            }}
          </button>
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import FileSelector from "@/components/FileSelector.vue";
import {
  getTruck,
  saveTruck,
  deleteTruck,
  truckAssetUrl,
  SPEC_SECTIONS,
  TRUCKS_SITE_URL
} from "@/api/store/TrucksService.js";

function emptyTruck() {
  return {
    id: null,
    seriesId: null,
    name: "",
    slug: "",
    description: "",
    imageUrl: null,
    datasheetUrl: null,
    featured: "0",
    sortOrder: 0,
    status: "1",
    metaTitle: "",
    metaDescription: "",
    specs: [],
    images: []
  };
}

export default {
  name: "TruckEditorModal",
  components: { FileSelector },
  props: {
    truckId: { type: [String, Number], default: null },
    series: { type: Array, default: () => [] }
  },
  data() {
    return {
      isOpen: true,
      siteUrl: TRUCKS_SITE_URL,
      truck: null,
      original: null,
      section: "general",
      saving: false,
      showFileSelector: false,
      fileTarget: null,
      tabs: [
        { value: "general", label: "General" },
        { value: "images", label: "Fotos" },
        { value: "specs", label: "Ficha" },
        { value: "seo", label: "SEO" }
      ]
    };
  },
  computed: {
    pending() {
      return JSON.stringify(this.truck) !== this.original;
    },
    slugChanged() {
      if (!this.truck.id || !this.original) return false;
      return JSON.parse(this.original).slug !== this.truck.slug;
    },
    specGroups() {
      return SPEC_SECTIONS.map(section => {
        const indexes = [];
        this.truck.specs.forEach((spec, index) => {
          if (spec.section === section.value) indexes.push(index);
        });
        return {
          ...section,
          rows: indexes.map((index, i) => ({
            index,
            first: i === 0,
            last: i === indexes.length - 1
          }))
        };
      });
    }
  },
  watch: {
    isOpen(val) {
      if (!val) this.$emit("close");
    },
    showFileSelector(val) {
      if (!val) this.fileTarget = null;
    }
  },
  async mounted() {
    if (!this.truckId) {
      this.setTruck(emptyTruck());
      return;
    }
    try {
      this.setTruck(await getTruck(this.truckId));
    } catch (e) {
      this.notify("No se pudo cargar el camión", "error");
      this.isOpen = false;
    }
  },
  methods: {
    assetUrl: truckAssetUrl,
    setTruck(truck) {
      this.truck = {
        ...emptyTruck(),
        ...truck,
        sortOrder: Number(truck.sortOrder) || 0,
        specs: (truck.specs || []).map(({ section, label, value }) => ({
          section,
          label,
          value
        })),
        images: (truck.images || []).map(({ url, alt }) => ({ url, alt }))
      };
      this.original = JSON.stringify(this.truck);
    },
    pickFile(target) {
      this.fileTarget = target;
      this.showFileSelector = true;
    },
    onFile(file) {
      const url = file.s3Url || file.url;
      if (!url) return;
      if (this.fileTarget === "datasheet") {
        this.truck.datasheetUrl = url;
      } else {
        this.truck.images.push({ url, alt: this.truck.name });
        if (!this.truck.imageUrl) this.truck.imageUrl = url;
      }
      this.showFileSelector = false;
    },
    move(list, index, delta) {
      const [item] = list.splice(index, 1);
      list.splice(index + delta, 0, item);
    },
    removeImage(index) {
      const [image] = this.truck.images.splice(index, 1);
      if (image.url === this.truck.imageUrl) {
        this.truck.imageUrl = this.truck.images[0]?.url || null;
      }
    },
    addSpec(section) {
      this.truck.specs.push({ section, label: "", value: "" });
    },
    moveSpec(index, delta) {
      // Intercambia con la fila vecina de la misma sección.
      const specs = this.truck.specs;
      const section = specs[index].section;
      let other = index + delta;
      while (
        other >= 0 &&
        other < specs.length &&
        specs[other].section !== section
      ) {
        other += delta;
      }
      if (other < 0 || other >= specs.length) return;
      const current = specs[index];
      this.$set(specs, index, specs[other]);
      this.$set(specs, other, current);
    },
    async save() {
      if (!this.truck.name.trim()) {
        this.notify("El nombre es obligatorio", "error");
        this.section = "general";
        return;
      }
      this.saving = true;
      try {
        const res = await saveTruck(this.truck);
        if (res.status !== "success") throw new Error(res.message);
        this.setTruck(res.truck);
        this.$emit("saved", res.truck);
        this.notify("Camión guardado", "success");
      } catch (e) {
        this.notify(
          e.response?.data?.message || e.message || "Error al guardar",
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      if (
        !confirm(
          `¿Eliminar ${this.truck.name}? Se pierden su ficha y sus fotos.`
        )
      )
        return;
      this.saving = true;
      try {
        await deleteTruck(this.truck.id);
        this.notify("Camión eliminado", "success");
        this.$emit("deleted");
      } catch (e) {
        this.notify("No se pudo eliminar", "error");
      } finally {
        this.saving = false;
      }
    },
    notify(message, type) {
      this.$toast.open({ message, type, position: "top-right" });
    }
  }
};
</script>

<style lang="scss" scoped>
.field-label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}
.field {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm;
  &:focus {
    @apply outline-none border-red-400;
  }
}
.btn-secondary {
  @apply px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg;
  &:hover {
    @apply bg-gray-50;
  }
}
:deep(.modal-content) {
  max-width: 1000px;
}
</style>
